import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Media, Row, Spinner, Table } from "reactstrap";
import { Activity, Award, Link2, RefreshCw, Users } from "react-feather";

import { apiFetch, API } from "../services/api";

// Recap Strava : comptes reliés, paires suivies (usure) et défi chaussettes.
// Données : GET /strava-stats (back_api, recalculé au plus une fois par heure
// depuis strava_athletes, strava_tokens, strava_shoe_wear, strava_km_challenge ;
// la table des courses n'est pas lue).

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const fmtInt = (n) => (n == null ? "—" : Math.round(n).toLocaleString("fr-FR"));
const fmtKm = (n) => (n == null ? "—" : Math.round(n).toLocaleString("fr-FR") + " km");
const pct = (a, b) => (!b ? "—" : Math.round((a / b) * 100) + " %");
const monthLabel = (m) => MONTHS[parseInt(m.slice(5, 7), 10) - 1] + " " + m.slice(2, 4);

function Kpi({ icon: Icon, color, label, value, hint }) {
    return (
        <Col md="3" sm="6">
            <Card className="flex-fill">
                <CardBody className="py-4">
                    <Media>
                        <div className="d-inline-block mt-2 mr-3">
                            <Icon className={"feather-lg text-" + color} />
                        </div>
                        <Media body>
                            <h3 className="mb-2">{value}</h3>
                            <div className="mb-0">{label}</div>
                            {hint ? <div className="text-muted small">{hint}</div> : null}
                        </Media>
                    </Media>
                </CardBody>
            </Card>
        </Col>
    );
}

function barChart(items, color, unit) {
    return {
        series: [{ name: unit, data: items.map((x) => x.count) }],
        options: {
            chart: { toolbar: { show: false } },
            colors: [color],
            plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 2 } },
            dataLabels: { enabled: true, style: { fontSize: "11px" } },
            xaxis: { categories: items.map((x) => x.label) },
            legend: { show: false },
        },
    };
}

function CountTable({ title, rows, unit }) {
    return (
        <Card className="flex-fill">
            <CardHeader><CardTitle tag="h5" className="mb-0">{title}</CardTitle></CardHeader>
            <Table size="sm" className="mb-0">
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.label}><td>{r.label}</td><td className="text-right">{fmtInt(r.count)} {unit}</td></tr>
                    ))}
                </tbody>
            </Table>
        </Card>
    );
}

export function StravaRecap() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        setLoading(true);
        apiFetch(API.back + "/strava-stats" + (refresh ? "?refresh=1" : ""), { method: "GET" })
            .then((r) => (r.ok ? r.json() : r.json().then((b) => Promise.reject(new Error(b.message || r.status)))))
            .then((d) => { setData(d); setError(""); })
            .catch((e) => setError(String(e.message || e)))
            .finally(() => setLoading(false));
    }, [refresh]);

    const monthly = useMemo(() => {
        const m = data ? data.accounts.monthly || [] : [];
        const labels = m.map((x) => monthLabel(x.month));
        const values = m.map((x) => x.count);
        if (data && data.accounts.sinceBackfill) {
            labels.push("depuis le 07/10*");
            values.push(data.accounts.sinceBackfill);
        }
        return {
            series: [{ name: "Comptes reliés", data: values }],
            options: {
                chart: { toolbar: { show: false } },
                colors: [({ dataPointIndex }) => (dataPointIndex === values.length - 1 && data && data.accounts.sinceBackfill ? "#FC4C02" : "#3B7DDD")],
                plotOptions: { bar: { columnWidth: "60%", borderRadius: 3, distributed: true } },
                dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#1F2937"] }, offsetY: -18 },
                legend: { show: false },
                xaxis: { categories: labels },
                yaxis: { labels: { formatter: (v) => Math.round(v) } },
            },
        };
    }, [data]);

    if (loading && !data) {
        return <div className="text-center my-4"><Spinner color="primary" /></div>;
    }
    if (error && !data) {
        return <Card body className="mb-4 text-danger">Recap Strava indisponible : {error}</Card>;
    }

    const a = data.accounts;
    const s = data.shoes;
    const ch = (data.challenges || [])[0];
    const usure = [
        { label: "< 80 % du seuil", count: s.pairs - s.over80 - s.over100 },
        { label: "80-100 % (à surveiller)", count: s.over80 },
        { label: "≥ 100 % (à remplacer)", count: s.over100 },
    ];

    return (
        <div className="mb-4">
            <Row className="mb-2">
                <Col>
                    <h3 className="mb-0">Recap Strava</h3>
                    <div className="text-muted small">
                        Calculé le {new Date(data.computedAt).toLocaleString("fr-FR")} (cache 1 h)
                        <RefreshCw size={14} className="ml-2" style={{ cursor: "pointer" }} onClick={() => setRefresh((n) => n + 1)} />
                        {loading ? <Spinner size="sm" className="ml-2" /> : null}
                    </div>
                </Col>
            </Row>

            <Row>
                <Kpi icon={Link2} color="primary" label="Comptes Strava reliés" value={fmtInt(a.connected)}
                     hint={fmtInt(a.disconnected) + " déconnectés depuis"} />
                <Kpi icon={Users} color="info" label="Profil des reliés"
                     value={pct(a.women, a.men + a.women) + " de femmes"}
                     hint={fmtInt(a.men) + " H · " + fmtInt(a.women) + " F · " + pct(a.premium, a.connected) + " abonnés Strava payants"} />
                <Kpi icon={Activity} color="warning" label="Paires suivies"
                     value={fmtInt(s.pairs)}
                     hint={s.avgPerRunner + " par coureur · " + fmtInt(s.runners) + " coureurs · médiane " + fmtKm(s.medianKm)} />
                <Kpi icon={Award} color="danger" label="Paires à remplacer (≥ seuil)"
                     value={fmtInt(s.over100)}
                     hint={fmtInt(s.over80) + " entre 80 et 100 % · " + pct(s.matched, s.pairs) + " rapprochées d'une fiche TRC"} />
            </Row>

            <Row>
                <Col lg="8" className="d-flex">
                    <Card className="flex-fill w-100">
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">Comptes reliés par mois de connexion</CardTitle>
                            <div className="text-muted small">
                                * Le 07/10/2026, la relecture de tous les profils a daté du jour les comptes historiques sans date :
                                la dernière barre mêle nouveaux comptes et rattrapage. {a.undated ? fmtInt(a.undated) + " comptes sans date." : ""}
                            </div>
                        </CardHeader>
                        <CardBody><Chart options={monthly.options} series={monthly.series} type="bar" height={280} /></CardBody>
                    </Card>
                </Col>
                <Col lg="4" className="d-flex">
                    <CountTable title="Villes des comptes reliés" rows={a.topCities || []} unit="" />
                </Col>
            </Row>

            <Row>
                <Col lg="6" className="d-flex">
                    <Card className="flex-fill w-100">
                        <CardHeader><CardTitle tag="h5" className="mb-0">Marques des paires suivies</CardTitle></CardHeader>
                        <CardBody>{(() => { const c = barChart(s.topBrands || [], "#FC4C02", "Paires"); return <Chart options={c.options} series={c.series} type="bar" height={380} />; })()}</CardBody>
                    </Card>
                </Col>
                <Col lg="6" className="d-flex">
                    <Card className="flex-fill w-100">
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">Kilométrage des paires</CardTitle>
                            <div className="text-muted small">Moyenne {fmtKm(s.avgKm)} · médiane {fmtKm(s.medianKm)} · {fmtInt(s.retired)} paires retirées</div>
                        </CardHeader>
                        <CardBody>
                            {(() => { const c = barChart(s.kmBuckets || [], "#3B7DDD", "Paires"); return <Chart options={c.options} series={c.series} type="bar" height={220} />; })()}
                            <Table size="sm" className="mb-0 mt-2">
                                <tbody>
                                    {usure.map((u) => <tr key={u.label}><td>{u.label}</td><td className="text-right">{fmtInt(u.count)}</td></tr>)}
                                    <tr className="text-muted"><td>Paliers franchis depuis le suivi (80 % / 100 %)</td><td className="text-right">{fmtInt(s.warned)} / {fmtInt(s.worn)}</td></tr>
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg="7" className="d-flex">
                    <Card className="flex-fill w-100">
                        <CardHeader><CardTitle tag="h5" className="mb-0">Modèles les plus portés</CardTitle></CardHeader>
                        <Table size="sm" responsive className="mb-0">
                            <thead><tr><th>Modèle</th><th className="text-right">Paires</th><th className="text-right">Coureurs</th><th className="text-right">Km moyens</th><th>Fiche TRC</th></tr></thead>
                            <tbody>
                                {(s.topModels || []).map((m) => (
                                    <tr key={m.label}>
                                        <td>{m.label}</td>
                                        <td className="text-right">{fmtInt(m.pairs)}</td>
                                        <td className="text-right">{fmtInt(m.runners)}</td>
                                        <td className="text-right">{fmtKm(m.avgKm)}</td>
                                        <td>{m.matched ? <Badge color="success">oui</Badge> : <Badge color="secondary">non</Badge>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
                <Col lg="5" className="d-flex">
                    <CountTable title="Paires sans fiche TRC (manques du catalogue ?)" rows={s.topUnmatched || []} unit="paires" />
                </Col>
            </Row>

            {ch ? (
                <Row>
                    <Col className="d-flex">
                        <Card className="flex-fill w-100">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">
                                    Défi chaussettes ({ch.id}) {ch.active ? <Badge color="success">actif</Badge> : <Badge color="secondary">inactif</Badge>}
                                </CardTitle>
                                <div className="text-muted small">
                                    Depuis le {new Date(ch.startAt).toLocaleString("fr-FR")} · objectif {fmtKm(ch.targetKm)} · {fmtInt(ch.participants)} coureurs avec au moins une course ·
                                    {" "}{fmtKm(ch.totalKm)} cumulés ({fmtKm(ch.avgKm)} en moyenne) · {fmtInt(ch.reached)} objectifs atteints ·
                                    {" "}{fmtInt(ch.awarded)} / {fmtInt(ch.stock)} paires attribuées
                                </div>
                            </CardHeader>
                            <CardBody>{(() => { const c = barChart(ch.progress || [], "#28a745", "Coureurs"); return <Chart options={c.options} series={c.series} type="bar" height={220} />; })()}</CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : null}
        </div>
    );
}
